/**
 * Channel filtering shared by the chat-preview sidebar and the Channels page.
 *
 * Both views filter the same `/channel-annotations/chats-with-annotations`
 * rows, so the predicate lives here rather than in either component.
 */

import { ChatWithAnnotation, DbStatus, MediaCategory } from '@/types/api';

/** `sorted` = an annotation row exists at all; `has_notes` = it carries text. */
export type ChannelStatusFilter = 'all' | 'sorted' | 'unsorted' | 'has_notes' | DbStatus;

export type ManuscriptFilter = 'all' | 'only' | 'excluded';

/** "Has at least one file of this kind", or `all` for no media constraint. */
export type MediaCategoryFilter = 'all' | MediaCategory;

export const MEDIA_CATEGORY_FILTERS: MediaCategory[] = [
  'pdf',
  'image',
  'video',
  'audio',
  'other',
];

/**
 * The per-category count column each filter reads. These come from
 * chat_stats_cache and only move when an admin runs
 * POST /channel-annotations/refresh-stats — stale by design. A media row with a
 * NULL mime_type lands in no bucket, so the five can sum to LESS than
 * `file_count`; never present them as a partition of it.
 */
const MEDIA_COUNT_FIELD: Record<MediaCategory, keyof ChatWithAnnotation> = {
  pdf: 'pdf_count',
  image: 'image_count',
  video: 'video_count',
  audio: 'audio_count',
  other: 'other_count',
};

export const mediaCategoryCount = (
  chat: ChatWithAnnotation,
  category: MediaCategory
): number => Number(chat[MEDIA_COUNT_FIELD[category]] ?? 0);

export interface ChannelFilters {
  search: string;
  status: ChannelStatusFilter;
  manuscript: ManuscriptFilter;
  media: MediaCategoryFilter;
}

export const ALL_CHANNEL_FILTERS: ChannelFilters = {
  search: '',
  status: 'all',
  manuscript: 'all',
  media: 'all',
};

/** Drives the active-dot on the filter button. Search has its own affordance. */
export const isChannelFilterActive = (filters: ChannelFilters): boolean =>
  filters.status !== 'all' || filters.manuscript !== 'all' || filters.media !== 'all';

export const filterChannels = <T extends ChatWithAnnotation>(
  chats: T[],
  filters: ChannelFilters
): T[] => {
  const query = filters.search.trim().toLowerCase();

  return chats.filter((chat) => {
    if (filters.manuscript === 'only' && !chat.is_manuscript_channel) return false;
    if (filters.manuscript === 'excluded' && chat.is_manuscript_channel) return false;

    switch (filters.status) {
      case 'all':
        break;
      case 'sorted':
        if (!chat.annotation) return false;
        break;
      case 'unsorted':
        if (chat.annotation) return false;
        break;
      case 'has_notes':
        if (!chat.annotation?.notes?.trim()) return false;
        break;
      default:
        if (chat.annotation?.db_status !== filters.status) return false;
    }

    if (filters.media !== 'all' && mediaCategoryCount(chat, filters.media) <= 0) return false;

    if (query) {
      const name = chat.chat_name?.toLowerCase() ?? '';
      // chat_id is nullable — searching must not match the string "null".
      const id = chat.chat_id === null ? '' : String(chat.chat_id);
      if (!name.includes(query) && !(id && id.includes(query))) return false;
    }

    return true;
  });
};
